import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { QueuedWrite } from '@/types';

/**
 * IndexedDB schema for offline support
 */
interface SundayBestDB extends DBSchema {
  queuedWrites: {
    key: string;
    value: QueuedWrite;
    indexes: { 'by-type': string };
  };
  cachedQuestionnaires: {
    key: string;
    value: {
      id: string;
      data: unknown;
      cachedAt: number;
    };
  };
  cachedPhotos: {
    key: string;
    value: {
      id: string;
      blob: Blob;
      sessionId: string;
      createdAt: number;
    };
    indexes: { 'by-session': string };
  };
}

let dbInstance: IDBPDatabase<SundayBestDB> | null = null;

/**
 * Initialize IndexedDB for offline storage
 */
export async function initDB(): Promise<IDBPDatabase<SundayBestDB>> {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = await openDB<SundayBestDB>('sunday-best-db', 1, {
    upgrade(db) {
      // Queued writes store
      if (!db.objectStoreNames.contains('queuedWrites')) {
        const queueStore = db.createObjectStore('queuedWrites', {
          keyPath: 'id',
        });
        queueStore.createIndex('by-type', 'type');
      }

      // Cached questionnaires store
      if (!db.objectStoreNames.contains('cachedQuestionnaires')) {
        db.createObjectStore('cachedQuestionnaires', {
          keyPath: 'id',
        });
      }

      // Cached photos store (for offline photo capture)
      if (!db.objectStoreNames.contains('cachedPhotos')) {
        const photoStore = db.createObjectStore('cachedPhotos', {
          keyPath: 'id',
        });
        photoStore.createIndex('by-session', 'sessionId');
      }
    },
  });

  return dbInstance;
}

/**
 * Get database instance
 */
export async function getDB(): Promise<IDBPDatabase<SundayBestDB>> {
  if (!dbInstance) {
    return await initDB();
  }
  return dbInstance;
}

/**
 * Add a write operation to the queue
 */
export async function queueWrite(write: QueuedWrite): Promise<void> {
  const db = await getDB();
  await db.add('queuedWrites', write);
}

/**
 * Get all queued writes
 */
export async function getQueuedWrites(): Promise<QueuedWrite[]> {
  const db = await getDB();
  return await db.getAll('queuedWrites');
}

/**
 * Remove a write from the queue after successful sync
 */
export async function removeQueuedWrite(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('queuedWrites', id);
}

/**
 * Cache questionnaire for offline use
 */
export async function cacheQuestionnaire(
  id: string,
  data: unknown
): Promise<void> {
  const db = await getDB();
  await db.put('cachedQuestionnaires', {
    id,
    data,
    cachedAt: Date.now(),
  });
}

/**
 * Get cached questionnaire
 */
export async function getCachedQuestionnaire(
  id: string
): Promise<unknown | null> {
  const db = await getDB();
  const cached = await db.get('cachedQuestionnaires', id);
  return cached?.data || null;
}

/**
 * Cache photo blob for offline sessions
 */
export async function cachePhoto(
  id: string,
  blob: Blob,
  sessionId: string
): Promise<void> {
  const db = await getDB();
  await db.put('cachedPhotos', {
    id,
    blob,
    sessionId,
    createdAt: Date.now(),
  });
}

/**
 * Get cached photos for a session
 */
export async function getCachedPhotos(sessionId: string): Promise<Blob[]> {
  const db = await getDB();
  const tx = db.transaction('cachedPhotos', 'readonly');
  const index = tx.store.index('by-session');
  const photos = await index.getAll(sessionId);
  return photos.map((p) => p.blob);
}

/**
 * Clear cached photos for a session after successful upload
 */
export async function clearCachedPhotos(sessionId: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction('cachedPhotos', 'readwrite');
  const index = tx.store.index('by-session');
  const photos = await index.getAll(sessionId);

  for (const photo of photos) {
    await tx.store.delete(photo.id);
  }

  await tx.done;
}
